/**
 * Game State Simulator
 *
 * Provides efficient game state simulation for AI search algorithms.
 * Creates new game states without modifying the original for minimax search.
 */

import { PlayerColor, BoardPosition, BOARD_SIZE, GameStatus } from '@/types/game-types';
import type { GameState } from '@/core/models/GameState';
import { getValidMoves } from '@/core/utils/move-validator';

/**
 * Simulated game state for AI search
 */
export interface SimulatedGameState {
  readonly board: Uint8Array;
  readonly currentPlayer: PlayerColor;
  readonly validMoves: Map<BoardPosition, readonly BoardPosition[]>;
  readonly scores: {
    readonly black: number;
    readonly white: number;
  };
  readonly gameOver: boolean;
  readonly consecutivePasses: number;
}

/**
 * Game state simulator for AI algorithms
 */
export class GameSimulator {
  /**
   * Convert a GameState to SimulatedGameState
   */
  static fromGameState(gameState: GameState): SimulatedGameState {
    // Check if game is over based on game status
    const gameOver = gameState.gameStatus === GameStatus.GAME_OVER ||
                     (gameState as any).gameOver === true ||
                     gameState.validMoves.size === 0;

    return {
      board: new Uint8Array(gameState.board),
      currentPlayer: gameState.currentPlayer,
      validMoves: new Map(gameState.validMoves),
      scores: { ...gameState.scores },
      gameOver,
      consecutivePasses: (gameState as any).consecutivePasses || 0,
    };
  }

  /**
   * Make a move and return new simulated game state
   */
  static makeMove(
    state: SimulatedGameState,
    position: BoardPosition,
  ): SimulatedGameState | null {
    // Validate move
    const flippedPositions = state.validMoves.get(position);
    if (!flippedPositions) {
      return null;
    }

    // Create new board
    const newBoard = new Uint8Array(state.board);

    // Place the piece
    newBoard[position] = state.currentPlayer;

    // Flip pieces
    for (const flippedPos of flippedPositions) {
      newBoard[flippedPos] = state.currentPlayer;
    }

    // Switch player
    const nextPlayer = state.currentPlayer === PlayerColor.BLACK
      ? PlayerColor.WHITE
      : PlayerColor.BLACK;

    // Calculate new scores
    const newScores = this.calculateScores(newBoard);

    // Find valid moves for next player
    const nextValidMoves = getValidMoves(newBoard, nextPlayer);

    // Check for consecutive passes
    let newConsecutivePasses = 0;
    let gameOver = false;

    if (nextValidMoves.size === 0) {
      // Next player has no moves, switch back
      const afterPassPlayer = nextPlayer === PlayerColor.BLACK
        ? PlayerColor.WHITE
        : PlayerColor.BLACK;

      const afterPassMoves = getValidMoves(newBoard, afterPassPlayer);

      if (afterPassMoves.size === 0) {
        // Game over - both players have no moves
        gameOver = true;
        newConsecutivePasses = 2;
      } else {
        // Switch back to original player
        newConsecutivePasses = 1;
        return {
          board: newBoard,
          currentPlayer: afterPassPlayer,
          validMoves: afterPassMoves,
          scores: newScores,
          gameOver: false,
          consecutivePasses: newConsecutivePasses,
        };
      }
    }

    return {
      board: newBoard,
      currentPlayer: nextPlayer,
      validMoves: nextValidMoves,
      scores: newScores,
      gameOver,
      consecutivePasses: newConsecutivePasses,
    };
  }

  /**
   * Make a pass (skip turn) and return new game state
   */
  static makePass(state: SimulatedGameState): SimulatedGameState {
    const nextPlayer = state.currentPlayer === PlayerColor.BLACK
      ? PlayerColor.WHITE
      : PlayerColor.BLACK;

    const nextValidMoves = getValidMoves(state.board, nextPlayer);
    const newConsecutivePasses = state.consecutivePasses + 1;

    return {
      board: state.board, // Board unchanged
      currentPlayer: nextPlayer,
      validMoves: nextValidMoves,
      scores: state.scores, // Scores unchanged
      gameOver: newConsecutivePasses >= 2 || nextValidMoves.size === 0,
      consecutivePasses: newConsecutivePasses,
    };
  }

  /**
   * Calculate scores from board state
   */
  private static calculateScores(board: Uint8Array): { black: number; white: number } {
    let black = 0;
    let white = 0;

    for (let i = 0; i < board.length; i++) {
      if (board[i] === PlayerColor.BLACK) {
        black++;
      } else if (board[i] === PlayerColor.WHITE) {
        white++;
      }
    }

    return { black, white };
  }

  /**
   * Get all possible moves with their resulting states
   */
  static getAllPossibleMoves(
    state: SimulatedGameState,
  ): Array<{ move: BoardPosition; newState: SimulatedGameState }> {
    const moves: Array<{ move: BoardPosition; newState: SimulatedGameState }> = [];

    for (const move of state.validMoves.keys()) {
      const newState = this.makeMove(state, move);
      if (newState) {
        moves.push({ move, newState });
      }
    }

    return moves;
  }

  /**
   * Check if game is in endgame phase
   */
  static isEndgame(state: SimulatedGameState): boolean {
    let emptySquares = 0;
    for (let i = 0; i < state.board.length; i++) {
      if (state.board[i] === PlayerColor.EMPTY) {
        emptySquares++;
      }
    }
    return emptySquares <= 16;
  }

  /**
   * Check if game is in opening phase
   */
  static isOpening(state: SimulatedGameState): boolean {
    let occupiedSquares = 0;
    for (let i = 0; i < state.board.length; i++) {
      if (state.board[i] !== PlayerColor.EMPTY) {
        occupiedSquares++;
      }
    }
    return occupiedSquares <= 12; // First 8 moves after initial setup
  }

  /**
   * Get game phase as a ratio (0 = start, 1 = end)
   */
  static getGamePhaseRatio(state: SimulatedGameState): number {
    let occupiedSquares = 0;
    for (let i = 0; i < state.board.length; i++) {
      if (state.board[i] !== PlayerColor.EMPTY) {
        occupiedSquares++;
      }
    }
    return Math.min(1.0, (occupiedSquares - 4) / 60); // Start counting after initial 4 pieces
  }

  /**
   * Generate a hash key for the game state (for transposition tables)
   */
  static getStateHash(state: SimulatedGameState): string {
    // Create a hash based on board state and current player
    const boardHash = Array.from(state.board).join('');
    return `${boardHash}_${state.currentPlayer}_${state.consecutivePasses}`;
  }

  /**
   * Check if two states are equivalent
   */
  static statesEqual(state1: SimulatedGameState, state2: SimulatedGameState): boolean {
    if (state1.currentPlayer !== state2.currentPlayer) return false;
    if (state1.consecutivePasses !== state2.consecutivePasses) return false;

    for (let i = 0; i < state1.board.length; i++) {
      if (state1.board[i] !== state2.board[i]) {
        return false;
      }
    }

    return true;
  }

  /**
   * Copy a simulated game state
   */
  static copyState(state: SimulatedGameState): SimulatedGameState {
    return {
      board: new Uint8Array(state.board),
      currentPlayer: state.currentPlayer,
      validMoves: new Map(state.validMoves),
      scores: { ...state.scores },
      gameOver: state.gameOver,
      consecutivePasses: state.consecutivePasses,
    };
  }

  /**
   * Get detailed state information for debugging
   */
  static getStateInfo(state: SimulatedGameState): {
    pieceCount: { black: number; white: number; empty: number };
    gamePhase: 'opening' | 'midgame' | 'endgame';
    mobilityCount: number;
  } {
    let black = 0, white = 0, empty = 0;

    for (let i = 0; i < state.board.length; i++) {
      switch (state.board[i]) {
        case PlayerColor.BLACK: black++; break;
        case PlayerColor.WHITE: white++; break;
        case PlayerColor.EMPTY: empty++; break;
      }
    }

    let gamePhase: 'opening' | 'midgame' | 'endgame';
    if (empty > 40) gamePhase = 'opening';
    else if (empty > 16) gamePhase = 'midgame';
    else gamePhase = 'endgame';

    return {
      pieceCount: { black, white, empty },
      gamePhase,
      mobilityCount: state.validMoves.size,
    };
  }

  /**
   * Validate that a simulated state is consistent
   */
  static validateState(state: SimulatedGameState): boolean {
    try {
      // Check board size
      if (state.board.length !== 64) return false;

      // Check player colors are valid
      if (state.currentPlayer !== PlayerColor.BLACK &&
          state.currentPlayer !== PlayerColor.WHITE) return false;

      // Check scores match board
      const calculatedScores = this.calculateScores(state.board);
      if (calculatedScores.black !== state.scores.black ||
          calculatedScores.white !== state.scores.white) return false;

      // Check valid moves are actually valid
      for (const [position, flips] of state.validMoves.entries()) {
        if (position < 0 || position >= 64) return false;
        if (state.board[position] !== PlayerColor.EMPTY) return false;
        // Additional validation could check if flips are correct
      }

      return true;
    } catch (error) {
      return false;
    }
  }
}

/**
 * Export convenience functions
 */
export const fromGameState = GameSimulator.fromGameState;
export const makeMove = GameSimulator.makeMove;
export const makePass = GameSimulator.makePass;
export const getAllPossibleMoves = GameSimulator.getAllPossibleMoves;